import type { Message } from '@xsai/shared-chat'

export interface NonVisionModelCacheEntry {
  markedAt: number
}

export type NonVisionModelCache = Record<string, NonVisionModelCacheEntry>

export const NON_VISION_MODEL_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000

function normalizeToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_]+/g, '-')
}

function readStringList(value: unknown): string[] {
  if (typeof value === 'string') {
    return [value]
  }

  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is string => typeof item === 'string')
}

export function extractModelCapabilities(model: Record<string, unknown>): string[] | undefined {
  const capabilities = new Set(readStringList(model.capabilities).map(normalizeToken))
  const inputModalities = readStringList(model.input_modalities ?? model.inputModalities).map(normalizeToken)
  const outputModalities = readStringList(model.output_modalities ?? model.outputModalities).map(normalizeToken)

  if (inputModalities.includes('image')) {
    capabilities.add('image-input')
  }

  if (outputModalities.includes('image')) {
    capabilities.add('image-output')
  }

  return capabilities.size > 0 ? Array.from(capabilities) : undefined
}

function isObjectPart(part: unknown): part is Record<string, unknown> {
  return typeof part === 'object' && part !== null
}

function isTextPart(part: unknown): part is { type: 'text', text: string } {
  return isObjectPart(part) && part.type === 'text' && typeof part.text === 'string'
}

function isImageUrlPart(part: unknown): part is { type: 'image_url' } {
  return isObjectPart(part) && part.type === 'image_url'
}

export function getNonVisionModelCacheKey(providerId: string, model: string): string {
  return `${providerId.trim()}:${model.trim()}`.toLowerCase()
}

export function isNonVisionModelCached(
  cache: NonVisionModelCache,
  cacheKey: string,
  now = Date.now(),
): boolean {
  const entry = cache[cacheKey]
  if (!entry)
    return false

  return now - entry.markedAt < NON_VISION_MODEL_CACHE_TTL_MS
}

export function pruneExpiredNonVisionModelCache(
  cache: NonVisionModelCache,
  now = Date.now(),
): NonVisionModelCache {
  return Object.fromEntries(
    Object.entries(cache).filter(([, entry]) => now - entry.markedAt < NON_VISION_MODEL_CACHE_TTL_MS),
  )
}

export function markNonVisionModelCached(
  cache: NonVisionModelCache,
  cacheKey: string,
  now = Date.now(),
): NonVisionModelCache {
  return {
    ...pruneExpiredNonVisionModelCache(cache, now),
    [cacheKey]: { markedAt: now },
  }
}

export function buildVisionFallbackNotice(imageCount: number): string {
  return `[系统说明]: 上文包含 ${imageCount} 张图片，当前模型无法直接识别图片内容。`
}

export function hasImageUrlContent(content: Message['content']): boolean {
  return Array.isArray(content) && content.some(isImageUrlPart)
}

export function hasImageUrlMessage(messages: Message[]): boolean {
  return messages.some(message => hasImageUrlContent(message.content))
}

export function isVisionUnsupportedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  const normalizedMessage = message.toLowerCase()

  // 排除服务端错误（500/502/503/超时），这些不代表模型不支持识图
  const isServerError = (/\b(?:500|502|503)\b/.test(normalizedMessage)
    && (normalizedMessage.includes('internal server error')
      || normalizedMessage.includes('bad gateway')
      || normalizedMessage.includes('service unavailable')
      || normalizedMessage.includes('status')
      || normalizedMessage.includes('server error')
      || normalizedMessage.includes('http')))
    || normalizedMessage.includes('timeout')
    || normalizedMessage.includes('bad gateway')
  if (isServerError)
    return false

  const mentionsImageInput = normalizedMessage.includes('image')
    || normalizedMessage.includes('vision')
    || normalizedMessage.includes('multimodal')
    || normalizedMessage.includes('modalit')
    || normalizedMessage.includes('image_url')

  const saysUnsupported = normalizedMessage.includes('not support')
    || normalizedMessage.includes('unsupported')
    || normalizedMessage.includes('does not support')
    || normalizedMessage.includes('only text')
    || normalizedMessage.includes('text only')
    || normalizedMessage.includes('invalid content type')
    || normalizedMessage.includes('cannot process')
    || normalizedMessage.includes('not capable')

  return mentionsImageInput && saysUnsupported
}

export function downgradeMultimodalContentForNonVisionModel(content: Message['content']): Message['content'] {
  if (typeof content === 'string' || !Array.isArray(content)) {
    return content
  }

  const imageCount = content.filter(isImageUrlPart).length
  if (imageCount === 0) {
    return content
  }

  const textContent = content
    .filter(isTextPart)
    .map(part => part.text.trim())
    .filter(Boolean)
    .join('\n')

  const fallbackNotice = buildVisionFallbackNotice(imageCount)
  return textContent ? `${textContent}\n${fallbackNotice}` : fallbackNotice
}

export function sanitizeMessagesForNonVisionModel(messages: Message[]): Message[] {
  return messages.map((message): Message => {
    const sanitizedContent = downgradeMultimodalContentForNonVisionModel(message.content)
    if (sanitizedContent === message.content) {
      return message
    }

    return {
      ...message,
      content: sanitizedContent,
    } as Message
  })
}
